<!-- Backdrop — click closes the panel. Always rendered when open so the
     fade-in animation lands cleanly. -->
@if (projectId()) {
  <div class="project-backdrop"
       (click)="shellState.closeProjectDetail()"
       aria-hidden="true"></div>
}

<aside class="project-panel"
       [class.is-open]="!!projectId()"
       role="dialog"
       aria-modal="true"
       [attr.aria-label]="'flow.project_detail_panel.project_detail' | transloco"
       [attr.aria-hidden]="projectId() ? null : 'true'"
       [imFocusTrap]="!!projectId()"
       [imFocusTrapAutoFocus]="'first'"
       data-testid="project-detail-panel">

  @if (project(); as p) {
    <!-- ─── Header ─────────────────────────────────────── -->
    <header class="project-panel__header">
      <div class="project-panel__title-block">
        @if (editingName()) {
          <input
            class="project-panel__title-input"
            type="text"
            [value]="nameDraft()"
            (input)="nameDraft.set($any($event.target).value)"
            (keydown.enter)="commitNameEdit()"
            (keydown.escape)="cancelNameEdit()"
            (blur)="commitNameEdit()"
            maxlength="96"
            autofocus />
        } @else {
          <paper-button
            class="project-panel__title ph-mask"
            intent="ghost"
            size="md"
            (click)="beginNameEdit()"
            [title]="'flow.project_detail_panel.click_to_rename' | transloco">
            @if (p.icon) {
              <span class="project-panel__title-icon">{{ p.icon }}</span>
            }
            {{ p.name }}
            <paper-icon name="edit" [size]="13" />
          </paper-button>
        }
        <p class="project-panel__subtitle">
          {{ p.meetingCount }}
          {{ p.meetingCount === 1 ? 'meeting' : 'meetings' }}
          @if (memberCount(p) > 0) {
            · {{ memberCount(p) }}
            {{ memberCount(p) === 1 ? 'member' : 'members' }}
          }
          @if (p.archivedAt) {
            · <span class="project-panel__archived-tag">{{ 'flow.project_detail_panel.archived' | transloco }}</span>
          }
        </p>
      </div>
      <div class="project-panel__actions">
        <paper-button
          class="project-panel__action-btn"
          intent="ghost"
          size="sm"
          shape="icon"
          [icon]="p.archivedAt ? 'rotate-ccw' : 'archive'"
          [iconSize]="14"
          [title]="p.archivedAt ? 'Restore project' : 'Archive project'"
          [ariaLabel]="p.archivedAt ? 'Restore project' : 'Archive project'"
          (click)="toggleArchive()" />
        <paper-button
          class="project-panel__action-btn"
          intent="ghost"
          size="sm"
          shape="icon"
          icon="x"
          [iconSize]="14"
          [ariaLabel]="'flow.project_detail_panel.close_panel' | transloco"
          (click)="shellState.closeProjectDetail()" />
      </div>
    </header>

    <div class="project-panel__body">
      <!-- ─── Description ────────────────────────────────── -->
      <!-- Rich text via the shared paper-rich-text editor (TipTap +
           StarterKit). Read mode renders the saved HTML through
           DomSanitizer.bypassSecurityTrustHtml — safe because every
           save is schema-filtered by TipTap before persistence. -->
      <section class="project-panel__section">
        <h3 class="project-panel__section-label">
          {{ 'flow.project_detail_panel.description' | transloco }}
          @if (!editingDescription() && hasDescription()) {
            <paper-button
              class="project-panel__section-edit"
              intent="secondary"
              size="xs"
              shape="pill"
              icon="edit"
              [iconSize]="11"
              [ariaLabel]="'flow.project_detail_panel.edit_description' | transloco"
              (click)="beginDescriptionEdit()">
              {{ 'flow.project_detail_panel.edit' | transloco }}
            </paper-button>
          }
        </h3>
        @if (editingDescription()) {
          <!-- ph-block: a project description is rich HTML, and an editor
               or a rendered link carries it in attributes too. -->
          <paper-rich-text
            class="project-panel__description-editor ph-block"
            [value]="descriptionDraft()"
            [placeholder]="'flow.project_detail_panel.what_are_you_trying_to_achieve' | transloco"
            (valueChange)="descriptionDraft.set($event)" />
          <div class="project-panel__description-actions">
            <paper-button
              class="project-panel__description-cancel"
              intent="secondary"
              size="sm"
              (click)="cancelDescriptionEdit()">
              {{ 'flow.project_detail_panel.cancel' | transloco }}
            </paper-button>
            <paper-button
              class="project-panel__description-save"
              intent="primary"
              size="sm"
              (click)="commitDescriptionEdit()">
              {{ 'flow.project_detail_panel.save' | transloco }}
            </paper-button>
          </div>
        } @else if (hasDescription()) {
          <div
            class="project-panel__description rich-content ph-block"
            [innerHTML]="safeDescription()"></div>
        } @else {
          <paper-button
            class="project-panel__description-empty"
            intent="secondary"
            size="sm"
            icon="plus"
            [iconSize]="12"
            [block]="true"
            (click)="beginDescriptionEdit()"
            [title]="'flow.project_detail_panel.click_to_add_a_description' | transloco">
            {{ 'flow.project_detail_panel.add_a_description_paragraphs_bullets_and' | transloco }}
          </paper-button>
        }
      </section>

      <!-- ─── Knowledge ──────────────────────────────────── -->
      <!-- The project anchors a Knowledge workspace + folder.
           Recordings added via the Flow card auto-route into the
           folder; manual pages created inside it inherit the project
           link via createPage's parent-properties propagation. -->
      <section class="project-panel__section">
        <h3 class="project-panel__section-label">
          <paper-icon name="layers" [size]="11" />
          {{ 'flow.project_detail_panel.knowledge' | transloco }}
          @if (p.knowledgeWorkspaceId) {
            <paper-button
              class="project-panel__section-edit"
              intent="secondary"
              size="xs"
              shape="pill"
              icon="x"
              [iconSize]="11"
              [ariaLabel]="'flow.project_detail_panel.unlink_workspace' | transloco"
              [title]="'flow.project_detail_panel.unlink_this_knowledge_workspace_from_the' | transloco"
              (click)="unlinkWorkspace()">
              {{ 'flow.project_detail_panel.unlink' | transloco }}
            </paper-button>
          }
        </h3>

        @if (knowledgeJustLinkedName(); as linked) {
          <!-- Brief in-panel success affordance after a successful
               link/create. Auto-clears in 1.5s. -->
          <div class="project-panel__knowledge-success" role="status" aria-live="polite">
            <span class="project-panel__knowledge-success-tick">
              <paper-icon name="check" [size]="12" />
            </span>
            <span>{{ 'flow.project_detail_panel.linked_to' | transloco }} <strong class="ph-mask">{{ linked }}</strong></span>
          </div>
        } @else if (p.knowledgeWorkspaceId) {
          <!-- Linked state. -->
          <div class="project-panel__knowledge-linked">
            <paper-button
              class="project-panel__knowledge-card"
              intent="secondary"
              size="sm"
              [block]="true"
              (click)="openProjectFolder()">
              <span class="project-panel__knowledge-icon">
                <paper-icon name="layers" [size]="14" />
              </span>
              <span class="project-panel__knowledge-card-text">
                <span class="project-panel__knowledge-name ph-mask">
                  {{ p.knowledgeWorkspaceName || 'Workspace' }}
                </span>
                <span class="project-panel__knowledge-meta">
                  {{ p.knowledgePageCount }}
                  {{ p.knowledgePageCount === 1 ? 'page' : 'pages' }}
                </span>
              </span>
              <paper-icon class="project-panel__knowledge-chevron" name="external-link" [size]="11" />
            </paper-button>

            @if (knowledgePagesLoading()) {
              <p class="project-panel__empty">{{ 'flow.project_detail_panel.loading_pages' | transloco }}</p>
            } @else if (knowledgePages().length > 0) {
              <!-- Page titles and meeting subjects below are masked in replay,
                   so no `title` tooltip repeats them: replay records
                   attributes verbatim. -->
              <ul class="project-panel__knowledge-page-list" role="list">
                @for (page of knowledgePages(); track page._id) {
                  <li>
                    <paper-button
                      class="project-panel__knowledge-page-row"
                      intent="ghost"
                      size="sm"
                      [block]="true"
                      (click)="openKnowledgePage(page._id)">
                      <span class="project-panel__knowledge-page-icon">
                        @if (page.icon) {
                          {{ page.icon }}
                        } @else if (page.isFlowDerived) {
                          <paper-icon name="mic" [size]="11" />
                        } @else {
                          <paper-icon name="folder" [size]="11" />
                        }
                      </span>
                      <span class="project-panel__knowledge-page-text">
                        <span class="project-panel__knowledge-page-title ph-mask">
                          {{ page.title }}
                        </span>
                        @if (page.updatedAt) {
                          <span class="project-panel__knowledge-page-meta">
                            {{ formatMeetingTime(page.updatedAt) }}
                          </span>
                        }
                      </span>
                      @if (page.isFlowDerived) {
                        <span class="project-panel__knowledge-page-badge">
                          <paper-icon name="mic" [size]="9" />
                          {{ 'flow.project_detail_panel.recorded' | transloco }}
                        </span>
                      }
                    </paper-button>
                  </li>
                }
              </ul>
              @if (p.knowledgePageCount > knowledgePages().length) {
                <paper-button
                  class="project-panel__knowledge-view-all"
                  intent="secondary"
                  size="xs"
                  [block]="true"
                  (click)="openProjectFolder()">
                  {{ 'flow.project_detail_panel.p_more_view_all_in_knowledge' | transloco:{ p: p.knowledgePageCount - knowledgePages().length } }}
                </paper-button>
              }
            } @else {
              <p class="project-panel__empty">
                {{ 'flow.project_detail_panel.no_pages_here_yet_add_flow' | transloco }}
              </p>
            }
          </div>
        } @else {
          <!-- Empty state. -->
          @if (workspacePickerOpen()) {
            <!-- Workspace picker — nested dialog inside the project
                 panel. Gets its own focus trap so Tab cycles within the
                 picker rather than escaping out into the panel's
                 header/body buttons. Esc closes the picker without
                 closing the parent panel (handled by stopPropagation
                 on the inner keydown). -->
            <div
              class="project-panel__knowledge-picker"
              role="dialog"
              aria-modal="true"
              [attr.aria-label]="'flow.project_detail_panel.link_knowledge_workspace' | transloco"
              [imFocusTrap]="workspacePickerOpen()"
              [imFocusTrapInitial]="workspacePickerSearchInput"
              (keydown.escape)="closeWorkspacePicker(); $event.stopPropagation()"
              data-testid="project-panel-knowledge-picker">
              <input
                #workspacePickerSearchInput
                type="text"
                class="project-panel__knowledge-picker-search"
                [placeholder]="'flow.project_detail_panel.search_workspaces' | transloco"
                [attr.aria-label]="'flow.project_detail_panel.search_workspaces_2' | transloco"
                [value]="workspacePickerQuery()"
                (input)="workspacePickerQuery.set($any($event.target).value)" />

              @let suggestions = workspacePickerSuggestions();
              @if (suggestions.length > 0) {
                <ul class="project-panel__knowledge-picker-list ph-mask" role="listbox">
                  @for (workspace of suggestions; track workspace._id) {
                    <li>
                      <paper-button
                        class="project-panel__knowledge-picker-option"
                        intent="ghost"
                        size="sm"
                        [block]="true"
                        (click)="commitLinkExisting(workspace)">
                        <span class="project-panel__knowledge-picker-icon">
                          @if (workspace.icon) {
                            {{ workspace.icon }}
                          } @else {
                            <paper-icon name="layers" [size]="12" />
                          }
                        </span>
                        <span>{{ workspace.name }}</span>
                      </paper-button>
                    </li>
                  }
                </ul>
              } @else if (workspacePickerQuery()) {
                <p class="project-panel__empty ph-mask">
                  {{ 'flow.project_detail_panel.no_workspaces_match_workspacepickerquery' | transloco:{ workspacePickerQuery: workspacePickerQuery() } }}
                </p>
              }

              <paper-button
                class="project-panel__knowledge-picker-create ph-mask"
                intent="secondary"
                size="xs"
                icon="plus"
                [iconSize]="11"
                [block]="true"
                (click)="commitCreateWorkspace()">
                {{ 'flow.project_detail_panel.create_new_p_workspace' | transloco:{ p: p.name } }}
              </paper-button>
              <paper-button
                class="project-panel__knowledge-picker-cancel"
                intent="ghost"
                size="xs"
                (click)="closeWorkspacePicker()">
                {{ 'flow.project_detail_panel.cancel_2' | transloco }}
              </paper-button>
            </div>
          } @else {
            <paper-button
              class="project-panel__knowledge-cta"
              intent="secondary"
              size="sm"
              [block]="true"
              (click)="openWorkspacePicker()">
              <paper-icon name="plus" [size]="12" />
              {{ 'flow.project_detail_panel.link_a_knowledge_workspace' | transloco }}
              <span class="project-panel__knowledge-cta-hint">
                {{ 'flow.project_detail_panel.recordings_notes_for_this_project_will' | transloco }}
              </span>
            </paper-button>
          }
        }
      </section>

      <!-- ─── Members ────────────────────────────────────── -->
      <section class="project-panel__section">
        <h3 class="project-panel__section-label">
          {{ 'flow.project_detail_panel.members' | transloco }}
          <span class="project-panel__section-count">{{ memberCount(p) }}</span>
        </h3>
        @if (memberCount(p) > 0) {
          <ul class="project-panel__member-list" role="list">
            @for (member of p.members; track member.userId || member.email) {
              <li class="project-panel__member">
                <span class="project-panel__member-avatar">
                  {{ (member.email || '?').charAt(0).toUpperCase() }}
                </span>
                <span class="project-panel__member-text">
                  <span class="project-panel__member-email">
                    {{ member.email }}
                  </span>
                  @if (member.addedAt) {
                    <span class="project-panel__member-meta">
                      {{ 'flow.project_detail_panel.added_formatdate' | transloco:{ formatDate: formatDate(member.addedAt) } }}
                    </span>
                  }
                </span>
              </li>
            }
          </ul>
        } @else {
          <p class="project-panel__empty">
            {{ 'flow.project_detail_panel.no_members_yet_cross_viewer_sharing' | transloco }}
          </p>
        }
      </section>

      <!-- ─── Meetings ───────────────────────────────────── -->
      <section class="project-panel__section">
        <h3 class="project-panel__section-label">
          {{ 'flow.project_detail_panel.meetings' | transloco }}
          <span class="project-panel__section-count">
            {{ p.meetingCount }}
          </span>
        </h3>
        @if (meetingsLoading()) {
          <p class="project-panel__empty">{{ 'flow.project_detail_panel.loading_meetings' | transloco }}</p>
        } @else if (meetings().length === 0) {
          <p class="project-panel__empty">
            {{ 'flow.project_detail_panel.no_meetings_tagged_into_this_project' | transloco }}
          </p>
        } @else {
          <ul class="project-panel__meeting-list" role="list">
            @for (m of meetings(); track m.meetingId) {
              <li class="project-panel__meeting">
                <paper-button
                  class="project-panel__meeting-row"
                  intent="secondary"
                  size="sm"
                  [block]="true"
                  [class.project-panel__meeting-row--auto]="m.isAutoAttached"
                  (click)="openMeeting(m.meetingId)">
                  <paper-icon name="calendar" [size]="13" />
                  <span class="project-panel__meeting-text">
                    <span class="project-panel__meeting-subject ph-mask">
                      {{ m.subject || 'Untitled meeting' }}
                    </span>
                    <span class="project-panel__meeting-meta">
                      {{ formatMeetingTime(m.startTime) }}
                      @if (m.isAutoAttached) {
                        · <span class="project-panel__meeting-auto-tag">{{ 'flow.project_detail_panel.from_series' | transloco }}</span>
                      }
                    </span>
                  </span>
                  @if (m.hasFlowRecording) {
                    <span class="project-panel__meeting-badge">
                      <paper-icon name="mic" [size]="9" />
                      {{ 'flow.project_detail_panel.recorded_2' | transloco }}
                    </span>
                  }
                </paper-button>
                <paper-button
                  class="project-panel__meeting-remove"
                  intent="ghost"
                  size="xs"
                  shape="icon"
                  icon="x"
                  [iconSize]="11"
                  [ariaLabel]="'flow.project_detail_panel.remove_from_project_aria' | transloco"
                  [title]="'flow.project_detail_panel.remove_from_project' | transloco"
                  (click)="removeMeeting(m.meetingId)" />
              </li>
            }
          </ul>
        }
      </section>

      <!-- ─── Managed recurring series ───────────────────── -->
      @if (p.seriesRules.length > 0) {
        <section class="project-panel__section">
          <h3 class="project-panel__section-label">
            {{ 'flow.project_detail_panel.auto_includes_recurring_series' | transloco }}
            <span class="project-panel__section-count">
              {{ p.seriesRules.length }}
            </span>
          </h3>
          <ul class="project-panel__series-list" role="list">
            @for (rule of p.seriesRules; track rule.seriesKey) {
              <li class="project-panel__series-rule">
                <div class="project-panel__series-rule-text">
                  <span class="project-panel__series-rule-title ph-mask">
                    <paper-icon name="recurring" [size]="11" />
                    {{ rule.seriesSubject || 'Recurring series' }}
                  </span>
                  <span class="project-panel__series-rule-meta">
                    {{ rule.attachedCount }}
                    {{ rule.attachedCount === 1 ? 'meeting attached' : 'meetings attached' }}
                  </span>
                </div>
                <paper-button
                  class="project-panel__meeting-remove"
                  intent="ghost"
                  size="xs"
                  shape="icon"
                  icon="x"
                  [iconSize]="11"
                  [ariaLabel]="'flow.project_detail_panel.stop_auto_including_series_aria' | transloco"
                  [title]="'flow.project_detail_panel.stop_auto_including_this_series' | transloco"
                  (click)="removeSeriesRule(rule.seriesKey)" />
              </li>
            }
          </ul>
        </section>
      }

      <p class="project-panel__footnote">
        {{ 'flow.project_detail_panel.created_formatdate_last_updated_formatdate2' | transloco:{ formatDate: formatDate(p.createdAt), formatDate2: formatDate(p.updatedAt) } }}
      </p>
    </div>
  } @else if (projectId()) {
    <!-- Cache miss — service still loading. Should be rare. -->
    <div class="project-panel__loading">
      <paper-icon name="refresh" [size]="16" class="project-panel__loading-icon" />
      {{ 'flow.project_detail_panel.loading_project' | transloco }}
    </div>
  }
</aside>
